// components/Navbar.tsx
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../utils/api";

interface UserData {
  _id?: string;
  name: string;
  email: string;
  role?: string;
}

const Navbar: React.FC = () => {
  // --- STATE MANAGEMENT ---
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileProgramOpen, setMobileProgramOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // State User
  const [user, setUser] = useState<UserData | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  // --- FUNGSI CLOSE MENU ---
  const closeMenu = () => {
    setIsMobileMenuOpen(false);
    setMobileProgramOpen(false);
  };

  // --- FUNGSI LOGOUT ---
  const performLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    window.dispatchEvent(new Event("storage"));
    navigate("/auth/Login");
  };

  const handleLogoutClick = () => {
    closeMenu();
    const confirmLogout = window.confirm("Are you sure you want to log out?");
    if (confirmLogout) {
      performLogout();
    }
  };

  // --- CEK LOGIN & VALIDASI KE SERVER ---
  const checkUserLogin = async () => {
    const storedUser = localStorage.getItem("user");

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);

        if (parsedUser._id) {
          const response = await fetch(
            `${API_BASE_URL}/users/${parsedUser._id}`,
          );
          if (!response.ok) {
            console.warn("User session invalid. Auto logging out.");
            performLogout();
          }
        }
      } catch (e) {
        console.error("Session check failed", e);
      }
    } else {
      setUser(null);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);

    checkUserLogin();
    window.addEventListener("storage", checkUserLogin);

    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("storage", checkUserLogin);
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  // --- HELPER UNTUK CLASS CSS ---
  const navItemClass = (path: string) => {
    const baseClass =
      "text-[14px] font-semibold font-montserrat transition-colors duration-200 cursor-pointer flex items-center py-2";
    const activeClass = "text-[#C60302] font-bold";
    const inactiveClass = "text-[#202020] hover:text-[#C60302]";

    return location.pathname === path
      ? `${baseClass} ${activeClass}`
      : `${baseClass} ${inactiveClass}`;
  };

  const isProgramActive =
    location.pathname.includes("/Course") || location.pathname.includes("/course");
  const programBtnClass = `text-[14px] font-semibold font-montserrat transition-colors duration-200 cursor-pointer flex items-center gap-1.5 py-2 ${isProgramActive
      ? "text-[#C60302] font-bold"
      : "text-[#202020] hover:text-[#C60302]"
    }`;

  return (
    <nav
      className={`sticky top-0 z-[999] w-full transition-all duration-300 font-montserrat border-b border-gray-100/80
      ${scrolled ? "bg-[#FDFDFD]/95 backdrop-blur-md shadow-sm py-3" : "bg-[#FDFDFD] py-4 lg:py-5"}`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="flex justify-between items-center relative">
          {/* --- KIRI: LOGO --- */}
          <div className="flex-shrink-0 flex items-center z-20">
            <Link to="/">
              <img
                src="/Fluenesia.png"
                alt="Fluenesia Logo"
                className="h-7 w-auto object-contain"
              />
            </Link>
          </div>

          { }
          <div className="hidden lg:flex items-center gap-8 xl:gap-10 absolute left-1/2 -translate-x-1/2 z-10">
            <Link to="/about" className={navItemClass("/about")}>
              About
            </Link>
            <div className="relative group h-full flex items-center">
              <button className={programBtnClass}>
                <span>Program</span>
                <i className="fas fa-chevron-down text-[10px] transition-transform duration-300 group-hover:rotate-180"></i>
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-2 w-80 z-[1000]">
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden ring-1 ring-black/5 p-2">
                  <div className="h-1 w-full bg-gradient-to-r from-[#C60302] to-[#ef4444] rounded-full mb-1"></div>
                  <Link
                    to="/course/Private"
                    className="group/item flex items-center gap-3.5 p-3 rounded-xl hover:bg-red-50/60 transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl bg-red-100 text-[#C60302] flex items-center justify-center text-base shadow-sm group-hover/item:bg-[#C60302] group-hover/item:text-white transition-all">
                      <i className="fas fa-user-graduate"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#202020] text-sm group-hover/item:text-[#C60302] transition-colors">
                        Private Class (1-on-1)
                      </h4>
                      <p className="text-xs text-[#6A6A6A]">Belajar intensif dedicated</p>
                    </div>
                  </Link>
                  <Link
                    to="/course/semiPrivate"
                    className="group/item flex items-center gap-3.5 p-3 rounded-xl hover:bg-red-50/60 transition-all mt-1"
                  >
                    <div className="w-10 h-10 rounded-xl bg-red-100 text-[#C60302] flex items-center justify-center text-base shadow-sm group-hover/item:bg-[#C60302] group-hover/item:text-white transition-all">
                      <i className="fas fa-users"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#202020] text-sm group-hover/item:text-[#C60302] transition-colors">
                        Semi Private Class
                      </h4>
                      <p className="text-xs text-[#6A6A6A]">Grup kecil interaktif</p>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
            <Link
              to="/Trial/TrialClass"
              className={navItemClass("/Trial/TrialClass")}
            >
              Register Class
            </Link>
          </div>

          {/* --- KANAN: LOGIN & START LEARNING / PROFIL --- */}
          <div className="flex items-center gap-3.5 z-20">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link
                    to="/admin/overview"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-white rounded-lg text-xs font-semibold hover:bg-gray-900 transition-colors mr-2 shadow-sm"
                  >
                    <i className="fas fa-tachometer-alt"></i>
                    Admin Panel
                  </Link>
                )}

                <div className="hidden md:flex items-center">
                  <Link
                    to="/user/Dashboard"
                    className="flex items-center gap-2.5 px-3 py-1.5 bg-white border border-gray-200 hover:border-[#C60302] rounded-full transition-all group shadow-sm"
                  >
                    <div className="w-8 h-8 bg-[#C60302] rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm group-hover:bg-[#a50201] transition-colors flex-shrink-0">
                      {user.name
                        ? user.name.charAt(0).toUpperCase()
                        : user.email
                          ? user.email.charAt(0).toUpperCase()
                          : "U"}
                    </div>
                    <span className="text-sm font-semibold text-[#202020] group-hover:text-[#C60302] transition-colors whitespace-nowrap">
                      {user.name || user.email || "User"}
                    </span>
                  </Link>
                </div>
              </>
            ) : (
              <div className="hidden lg:flex items-center gap-3">
                <Link
                  to="/auth/Login"
                  className="h-11 px-5 rounded-lg border-2 border-[#C60302] text-[#C60302] font-semibold text-sm hover:bg-red-50 transition-all duration-200 flex items-center justify-center"
                >
                  Login
                </Link>
                <Link
                  to="/auth/Register"
                  className="h-11 px-5 rounded-lg bg-[#C60302] text-white font-semibold text-sm shadow-sm hover:bg-[#a50201] transition-all duration-200 flex items-center justify-center"
                >
                  Start Learning
                </Link>
              </div>
            )}

            <button
              className="lg:hidden text-[#202020] p-2 focus:outline-none"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <i className="fas fa-bars text-2xl"></i>
            </button>
          </div>
        </div>

        {/* --- MOBILE SIDEBAR --- */}
        <div
          className={`fixed inset-0 bg-black/50 z-[1000] lg:hidden transition-opacity duration-300 ${isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
          onClick={closeMenu}
        ></div>

        <div
          className={`fixed top-0 right-0 h-full w-[85%] max-w-[300px] bg-white shadow-2xl z-[1001] transform transition-transform duration-300 ease-in-out lg:hidden flex flex-col ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          <div className="flex justify-between items-center p-5 border-b border-gray-100">
            <img
              src="/Fluenesia.png"
              alt="Fluenesia"
              className="h-4 w-auto object-contain"
            />
            <button
              onClick={closeMenu}
              className="text-gray-400 hover:text-[#B21318]"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-1">
            <Link
              to="/about"
              onClick={closeMenu}
              className="block px-4 py-3 rounded-lg hover:bg-gray-50 font-medium text-gray-700 hover:text-[#B21318]"
            >
              About
            </Link>
            <div>
              <button
                className="w-full flex justify-between items-center px-4 py-3 rounded-lg hover:bg-gray-50 font-medium text-gray-700 hover:text-[#B21318]"
                onClick={() => setMobileProgramOpen(!mobileProgramOpen)}
              >
                Program{" "}
                <i
                  className={`fas fa-chevron-down text-xs transition-transform ${mobileProgramOpen ? "rotate-180" : ""}`}
                ></i>
              </button>
              {mobileProgramOpen && (
                <div className="pl-4 space-y-1 mt-1">
                  <Link
                    to="/Course/Starter"
                    onClick={closeMenu}
                    className="block px-4 py-2 text-sm text-gray-600 hover:text-[#B21318]"
                  >
                    Starter
                  </Link>
                  <Link
                    to="/Course/Basic"
                    onClick={closeMenu}
                    className="block px-4 py-2 text-sm text-gray-600 hover:text-[#B21318]"
                  >
                    Basic
                  </Link>
                  <Link
                    to="/Course/Independent"
                    onClick={closeMenu}
                    className="block px-4 py-2 text-sm text-gray-600 hover:text-[#B21318]"
                  >
                    Independent
                  </Link>
                  <Link
                    to="/Course/Advance"
                    onClick={closeMenu}
                    className="block px-4 py-2 text-sm text-gray-600 hover:text-[#B21318]"
                  >
                    Advance
                  </Link>
                </div>
              )}
            </div>
            <Link
              to="/how-it-works"
              onClick={closeMenu}
              className="block px-4 py-3 rounded-lg hover:bg-gray-50 font-medium text-gray-700 hover:text-[#B21318]"
            >
              How It Works
            </Link>
            <Link
              to="/Trial/TrialClass"
              onClick={closeMenu}
              className="block px-4 py-3 rounded-lg hover:bg-gray-50 font-medium text-gray-700 hover:text-[#B21318]"
            >
              Trial Class
            </Link>
          </div>

          <div className="p-5 border-t border-gray-100 bg-gray-50">
            {user ? (
              <div className="flex flex-col space-y-2">
                <Link
                  to="/user/Dashboard"
                  onClick={closeMenu}
                  className="mb-2 px-2 group"
                >
                  <p className="font-bold text-gray-800 text-lg group-hover:text-[#B21318] transition-colors">
                    {user.name || user.email || "User"}
                  </p>
                  <p className="text-xs text-gray-500">{user.email || ""}</p>
                </Link>
                {user.role === "admin" && (
                  <Link
                    to="/admin/overview"
                    onClick={closeMenu}
                    className="flex items-center gap-3 px-3 py-3 text-sm font-bold text-gray-800 bg-gray-200 rounded-lg hover:bg-gray-300 mb-1"
                  >
                    <i className="fas fa-tachometer-alt w-5 text-center"></i>{" "}
                    Admin Panel
                  </Link>
                )}
                <Link
                  to="/user/Dashboard"
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-white hover:text-[#B21318] transition-colors"
                >
                  <i className="fas fa-book-reader w-5 text-center"></i>{" "}
                  Dashboard
                </Link>
                <div className="h-px bg-gray-200 my-2"></div>
                <button
                  onClick={handleLogoutClick}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-red-100 text-[#B21318] rounded-md font-bold text-sm hover:bg-red-200 transition-colors"
                >
                  <i className="fas fa-sign-out-alt"></i> Log Out
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3 font-montserrat">
                <Link
                  to="/auth/Login"
                  onClick={closeMenu}
                  className="w-full py-3 text-center border-2 border-[#C60302] text-[#C60302] rounded-lg font-semibold text-sm hover:bg-red-50"
                >
                  Login
                </Link>
                <Link
                  to="/auth/Register"
                  onClick={closeMenu}
                  className="w-full py-3 text-center bg-[#C60302] text-white rounded-lg font-semibold text-sm hover:bg-[#a50201] shadow-sm"
                >
                  Start Learning
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
